/**
 * CSP-safe drop-in replacement for AJV v6.
 *
 * AJV v6 uses new Function() to compile validators at runtime, which is
 * blocked by strict CSP. This wraps the `jsonschema` library (which
 * interprets schemas without code generation) behind AJV v6's API so
 * react-jsonschema-form works under a strict CSP.
 */

var Validator = require('jsonschema').Validator;

function AjvCspSafe(options) {
  this.errors = null;
  this._validator = new Validator();
  this._options = options || {};
}

AjvCspSafe.prototype.addFormat = function addFormat(name, format) {
  this._validator.customFormats[name] = format;
};

AjvCspSafe.prototype.addMetaSchema = function addMetaSchema(schemas) {
  var list = Array.isArray(schemas) ? schemas : [schemas];
  for (var i = 0; i < list.length; i++) {
    var s = list[i];
    if (!s) continue;
    var id = s.$id || s.id;
    if (id) this._validator.addSchema(s, id);
  }
};

AjvCspSafe.prototype.validate = function validate(schema, data) {
  this.errors = null;

  var result = this._validator.validate(data, schema);

  if (result.valid) return true;

  var useProperty = this._options.errorDataPath === 'property';
  this.errors = result.errors.map(function(e) {
    return transformError(e, useProperty);
  });
  return false;
};

// --- Error transformation from jsonschema format to AJV v6 format ---

function transformError(error, usePropertyPath) {
  var dataPath = toDataPath(error.path);

  // With errorDataPath:"property", AJV puts the missing property
  // in dataPath instead of the parent path.
  if (error.name === 'required' && usePropertyPath) {
    dataPath = dataPath + '.' + error.argument;
  }

  return {
    keyword: error.name,
    dataPath: dataPath,
    message: error.message,
    params: buildParams(error),
    schemaPath: buildSchemaPath(error),
  };
}

/**
 * Convert jsonschema path array to AJV dot-bracket notation.
 * e.g. ['foo', 0, 'bar'] -> '.foo[0].bar'
 */
function toDataPath(segments) {
  if (!segments || !segments.length) return '';
  return segments
    .map(function(s) {
      return typeof s === 'number' ? '[' + s + ']' : '.' + s;
    })
    .join('');
}

function buildParams(error) {
  switch (error.name) {
    case 'required':
      return { missingProperty: error.argument };
    case 'type':
      return {
        type: Array.isArray(error.argument) ? error.argument.join(',') : error.argument,
      };
    case 'enum':
      return { allowedValues: error.argument };
    case 'minimum':
      return { limit: error.argument, comparison: '>=', exclusive: false };
    case 'maximum':
      return { limit: error.argument, comparison: '<=', exclusive: false };
    case 'minLength':
    case 'maxLength':
    case 'minItems':
    case 'maxItems':
      return { limit: error.argument };
    case 'pattern':
      return { pattern: error.argument };
    case 'format':
      return { format: error.argument };
    case 'uniqueItems':
      return {
        i: error.argument && error.argument[0],
        j: error.argument && error.argument[1],
      };
    case 'additionalProperties':
      return { additionalProperty: error.argument };
    default:
      return {};
  }
}

function buildSchemaPath(error) {
  var segments = (error.path || []).map(function(p) {
    return typeof p === 'number' ? 'items' : 'properties/' + p;
  });
  var base = segments.length ? '#/' + segments.join('/') : '#';
  return base + '/' + error.name;
}

module.exports = AjvCspSafe;
